import React from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "./ui/Skeleton";
import { Card, CardContent } from "@/components/ui/Card";
import { ChartHeader } from "@/components/ChartHeader";
import { useQuery } from "@apollo/client";
import { useOrg } from "@/hooks/useOrg";
import { CURRENT_COHORT_YEAR } from "../api/queries/currentCohortYear";
import { FUNNEL_CHART_DATA } from "../api/queries/funnelChartData";
import { ProtectedStudentListLink } from "@/routes/buildStudentsListLink";
import {
  convertFunnelStatesGroups,
  FunnelMapKeys,
  groupStatesByFunnelGroup,
} from "../util/funnelGroups";
import { FilterState } from "./FilterSidebar/FilterState";
import { YesNoEnum } from "@/__generated__/types";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { useMouse } from "@/hooks/useMouse";
import { TooltipLineItem, ChartTooltipContent } from "./ChartTooltip";

export interface FunnelState {
  id: string;
  label: string;
  count: number;
  color?: string;
  groupKey: FunnelMapKeys<"CHARTS">;
}

interface CurrentFunnelStatesProps {
  title: string;
  subtitle: string;
  getSegmentLink: (state: FunnelState) => string;
}

const FUNNEL_STATE_CONFIG: {
  id: string;
  labelKey: string;
  groupKey: FunnelMapKeys<"CHARTS">;
  color: string;
}[] = [
  {
    id: "inquired",
    labelKey: "adoption.currentFunnelStates.funnelStates.inquired",
    groupKey: "inquired",
    color: "--color-chart-1",
  },
  {
    id: "applied",
    labelKey: "adoption.currentFunnelStates.funnelStates.applied",
    groupKey: "applied",
    color: "--color-chart-2",
  },
  {
    id: "admitted",
    labelKey: "adoption.currentFunnelStates.funnelStates.admitted",
    groupKey: "admitted",
    color: "--color-chart-3",
  },
  {
    id: "withdrawn",
    labelKey: "adoption.currentFunnelStates.funnelStates.withdrawn",
    groupKey: "commitmentWithdrawn",
    color: "--color-chart-4",
  },
  {
    id: "committed",
    labelKey: "adoption.currentFunnelStates.funnelStates.committed",
    groupKey: "committed",
    color: "--color-chart-5",
  },
];

export const CurrentFunnelStates: React.FC<CurrentFunnelStatesProps> = ({
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const { org, loading: orgIdLoading, error: orgIdError } = useOrg();

  const {
    data: cohortData,
    loading: cohortLoading,
    error: cohortError,
  } = useQuery(CURRENT_COHORT_YEAR);

  const cohortYear = cohortData?.currentCohortYear;
  const today = new Date();

  const {
    data: funnelQueryData,
    loading: funnelLoading,
    error: funnelError,
  } = useQuery(FUNNEL_CHART_DATA, {
    variables: {
      orgId: org?.id || "",
      date: today.toISOString().slice(0, 10), // YYYY-MM-DD
      filters: new FilterState({
        cohortYear: cohortYear ? [cohortYear.toString()] : [],
      }).getQueryFilter(),
    },
    skip: !org?.id || !cohortYear,
  });

  const getFunnelData = (): FunnelState[] => {
    let groupedData: Record<string, number> = {};

    if (
      funnelQueryData?.node &&
      funnelQueryData.node.__typename === "Organization"
    ) {
      const orgData = funnelQueryData.node;
      const inCommunityData = orgData.funnelChartData?.inCommunity || [];
      groupedData = groupStatesByFunnelGroup(
        inCommunityData,
        "CURRENT_FUNNEL_STATE_CHART",
      );
    }

    return FUNNEL_STATE_CONFIG.map((config) => ({
      id: config.id,
      groupKey: config.groupKey,
      label: t(config.labelKey),
      count: groupedData[config.groupKey] || 0,
      color: config.color,
    }));
  };

  const funnelData = getFunnelData().map((state) => ({
    ...state,
    name: state.label,
  }));

  const totalCount = funnelData.reduce((sum, state) => sum + state.count, 0);

  const hasActualError = orgIdError || cohortError || funnelError;
  const hasMissingDependencies =
    !orgIdLoading && !cohortLoading && (!org?.id || !cohortYear);
  const isLoading = orgIdLoading || cohortLoading || funnelLoading;

  if (isLoading) {
    return <FunnelStatesSkeleton title={title} subtitle={subtitle} />;
  }

  if (hasActualError || hasMissingDependencies) {
    return (
      <Card className="w-full">
        <ChartHeader {...{ title, subtitle: subtitle ?? "" }} />
        <CardContent>
          <div className="p-4 text-center text-primary">
            {t("adoption.currentFunnelStates.error.loadingError")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <ChartHeader {...{ title, subtitle: subtitle ?? "" }} />
      <CardContent>
        <ChartContent
          funnelData={funnelData}
          totalCount={totalCount}
          cohortYear={`${cohortYear}`}
        />
      </CardContent>
    </Card>
  );
};

const ChartContent = ({
  funnelData,
  totalCount,
  cohortYear,
}: {
  funnelData: (FunnelState & { name?: string })[];
  totalCount: number;
  cohortYear: string;
}) => {
  return (
    <>
      <div className="mt-4 flex h-8 gap-1">
        {funnelData.map((state) => (
          <FunnelStateLine
            key={state.id}
            state={state}
            cohortYear={cohortYear}
            totalCount={totalCount}
          />
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-12">
        {funnelData.map((state) => {
          const filterSidebarFunnelGroups = convertFunnelStatesGroups(
            [state.groupKey],
            "CHARTS",
            "SIDEBAR",
          );
          return (
            <ProtectedStudentListLink
              key={state.id}
              className="flex items-center gap-2"
              fs={
                new FilterState({
                  funnelStates: filterSidebarFunnelGroups,
                  cohortYear: [cohortYear],
                  inCommunity: YesNoEnum.Yes,
                })
              }
              title={`${state.name}: ${state.count}`}
              source={`Current Funnel States - ${state.name}`}
            >
              <div
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: `var(${state.color})` }}
              />
              <span className="text-sm font-normal">{state.name}</span>
              <span className="text-sm font-bold text-primary">
                {state.count}
              </span>
            </ProtectedStudentListLink>
          );
        })}
      </div>
    </>
  );
};

const FunnelStateLine = ({
  state,
  totalCount,
  cohortYear,
}: {
  state: FunnelState & { name?: string };
  totalCount: number;
  cohortYear: string;
}) => {
  const { ref, x, y } = useMouse<HTMLAnchorElement>();

  const filterSidebarFunnelGroups = convertFunnelStatesGroups(
    [state.groupKey],
    "CHARTS",
    "SIDEBAR",
  );
  const cssColorVar = `var(${state.color})`;
  return (
    <TooltipPrimitive.TooltipProvider>
      <TooltipPrimitive.Tooltip delayDuration={0}>
        <TooltipPrimitive.TooltipTrigger asChild>
          <ProtectedStudentListLink
            key={state.id}
            ref={ref}
            className="h-2 cursor-pointer rounded transition-all hover:opacity-90"
            fs={
              new FilterState({
                funnelStates: filterSidebarFunnelGroups,
                inCommunity: YesNoEnum.Yes,
                cohortYear: [cohortYear],
              })
            }
            style={{
              width: `${(state.count / totalCount) * 100}%`,
              backgroundColor: cssColorVar,
            }}
            source={`Current Funnel States - ${state.name}`}
          />
        </TooltipPrimitive.TooltipTrigger>
        <TooltipPrimitive.TooltipContent
          align="start"
          side="bottom"
          alignOffset={x}
          sideOffset={y + 10}
        >
          <ChartTooltipContent title={"Funnel State"}>
            <TooltipLineItem
              label={state.name || ""}
              color={cssColorVar}
              value={state.count}
            />
          </ChartTooltipContent>
        </TooltipPrimitive.TooltipContent>
      </TooltipPrimitive.Tooltip>
    </TooltipPrimitive.TooltipProvider>
  );
};

const FunnelStatesSkeleton = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => {
  return (
    <Card className="w-full">
      <ChartHeader {...{ title, subtitle: subtitle ?? "" }} />
      <CardContent>
        <div className="mt-4 flex h-8 gap-1">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-2 flex-1" />
          ))}
        </div>
        <div className="mb-4 flex flex-wrap gap-12">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-2">
              <Skeleton className="h-2 w-2 rounded-full" />
              <Skeleton className="h-4 w-30" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
